/* ==========================================================================
   Sitar — theme scripts
   Vanilla JS, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var Sitar = (window.Sitar = window.Sitar || {});

  /* ---------------------------------------------------------------- utils */
  Sitar.money = function (cents) {
    var format = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: format || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  Sitar.trapFocus = function (container) {
    var selectors =
      'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])';
    var nodes = Array.prototype.slice.call(container.querySelectorAll(selectors)).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!nodes.length) return function () {};
    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    function handler(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    window.setTimeout(function () {
      first.focus();
    }, 60);
    return function () {
      container.removeEventListener('keydown', handler);
    };
  };

  Sitar.debounce = function (fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  };

  Sitar.fetchSection = function (sectionId, url) {
    var target = url || window.location.pathname;
    var separator = target.indexOf('?') > -1 ? '&' : '?';
    return fetch(target + separator + 'section_id=' + sectionId)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  };

  /* -------------------------------------------------------------- overlay */
  function openOverlay(el) {
    if (!el) return;
    // Locking the body removes the scrollbar, which widens the viewport and
    // shifts every fixed element. Measure the gap and hand it to CSS.
    var gap = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', gap + 'px');
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    el._releaseFocus = Sitar.trapFocus(el);
  }

  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.style.setProperty('--scrollbar-width', '0px');
    el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    if (typeof el._releaseFocus === 'function') el._releaseFocus();
  }

  Sitar.openOverlay = openOverlay;
  Sitar.closeOverlay = closeOverlay;

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-overlay-open]');
    if (opener) {
      event.preventDefault();
      openOverlay(document.getElementById(opener.getAttribute('data-overlay-open')));
      return;
    }
    var closer = event.target.closest('[data-overlay-close]');
    if (closer) {
      event.preventDefault();
      closeOverlay(closer.closest('[data-overlay]'));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = document.querySelector('[data-overlay].is-open');
    if (open) closeOverlay(open);
  });

  /* ------------------------------------------------------------ mobile nav */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-submenu-toggle]');
    if (!toggle) return;
    event.preventDefault();
    var list = toggle.parentElement.querySelector('.mobile-nav__sublist');
    if (!list) return;
    var isOpen = list.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });

  /* ------------------------------------------------------------- accordion */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('.accordion__toggle');
    if (!toggle) return;
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    panel.setAttribute('data-open', expanded ? 'false' : 'true');
  });

  /* -------------------------------------------------------------- quantity */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('.quantity__button');
    if (!button) return;
    event.preventDefault();
    var wrapper = button.closest('.quantity');
    var input = wrapper.querySelector('.quantity__input');
    var step = button.getAttribute('data-action') === 'increase' ? 1 : -1;
    var min = parseInt(input.getAttribute('min') || '0', 10);
    var next = (parseInt(input.value, 10) || 0) + step;
    if (next < min) next = min;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------- announcement bar */
  function initAnnouncement() {
    document.querySelectorAll('[data-announcement]').forEach(function (bar) {
      var slides = bar.querySelectorAll('.announcement__slide');
      if (slides.length < 2) return;
      var index = 0;
      setInterval(function () {
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }, 5000);
    });
  }

  /* ----------------------------------------------------------- reveal on scroll */
  function initReveal() {
    if (!document.body.classList.contains('animate-enabled')) return;
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('.reveal').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------ sticky header */
  function initStickyHeader() {
    var header = document.querySelector('.header-wrapper--sticky');
    var transparent = document.querySelector('.header-group--transparent');
    if (!header && !transparent) return;

    // Re-pin the transparent header only once it has fully scrolled past, so it
    // doesn't flicker between overlay and solid while still partly on screen.
    var pinAfter = transparent ? transparent.offsetHeight + 80 : 0;

    function onScroll() {
      var y = window.scrollY;
      if (header && !transparent) header.classList.toggle('is-scrolled', y > 40);
      if (transparent) transparent.classList.toggle('is-pinned', y > pinAfter);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener(
      'resize',
      function () {
        if (transparent && !transparent.classList.contains('is-pinned')) {
          pinAfter = transparent.offsetHeight + 80;
        }
      },
      { passive: true }
    );
    onScroll();
  }

  /* ------------------------------------------------------------------ cart */
  Sitar.cart = {
    add: function (formData) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      });
    },
    change: function (payload) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (response) {
        return response.json();
      });
    },
    get: function () {
      return fetch('/cart.js').then(function (response) {
        return response.json();
      });
    }
  };

  /* Formats an amount in cents using the shop's money format string, so a
     price rebuilt in the browser after an AJAX cart update matches the one
     Liquid rendered on load. The format string is read off the element rather
     than assumed, which keeps the right symbol and separators for whichever
     market the shopper is in.

     Handles the four placeholders Shopify supports: {{amount}},
     {{amount_no_decimals}}, {{amount_with_comma_separator}} and
     {{amount_no_decimals_with_comma_separator}}. */
  Sitar.formatMoney = function (cents, format) {
    var pattern = format || '${{amount}}';
    var value = typeof cents === 'string' ? parseFloat(cents.replace('.', '')) : cents;
    if (isNaN(value) || value === null) value = 0;

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, placeholder) {
      var decimals = placeholder.indexOf('no_decimals') !== -1 ? 0 : 2;
      var comma = placeholder.indexOf('comma_separator') !== -1;

      var amount = (value / 100).toFixed(decimals);
      var parts = amount.split('.');
      var whole = parts[0];
      var fraction = parts[1];

      // Group the whole part in threes, then apply the separators this
      // placeholder asked for.
      whole = whole.replace(/(\d)(?=(\d{3})+$)/g, '$1,');
      if (comma) whole = whole.replace(/,/g, '.');

      if (!fraction) return whole;
      return whole + (comma ? ',' : '.') + fraction;
    });
  };

  /* The country picker is a <details>, so opening and choosing already work
     without this. All the script adds is the behaviour people expect of a
     dropdown: clicking away or pressing Escape closes it. Bound once on the
     document rather than per element, so a header re-rendered by the theme
     editor keeps working. */
  var countryPickerBound = false;
  function initCountryPicker() {
    if (countryPickerBound) return;
    countryPickerBound = true;

    function closeAll(except) {
      document.querySelectorAll('[data-country-picker][open]').forEach(function (node) {
        if (node !== except) node.removeAttribute('open');
      });
    }

    document.addEventListener('click', function (event) {
      var picker = event.target.closest ? event.target.closest('[data-country-picker]') : null;
      closeAll(picker);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = document.querySelector('[data-country-picker][open]');
      if (!open) return;
      open.removeAttribute('open');
      var summary = open.querySelector('summary');
      if (summary) summary.focus();
    });
  }

  Sitar.refreshCart = function (openDrawer) {
    return Promise.all([Sitar.cart.get(), Sitar.fetchSection('cart-drawer', '/')]).then(function (results) {
      var cart = results[0];
      var doc = results[1];

      var counts = document.querySelectorAll('[data-cart-count]');
      counts.forEach(function (node) {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;
      });

      var totals = document.querySelectorAll('[data-cart-total]');
      totals.forEach(function (node) {
        node.textContent = Sitar.formatMoney(cart.total_price, node.getAttribute('data-money-format'));
      });

      var freshDrawer = doc.querySelector('[data-cart-drawer]');
      var currentDrawer = document.querySelector('[data-cart-drawer]');
      if (freshDrawer && currentDrawer) {
        var wasOpen = currentDrawer.classList.contains('is-open');
        currentDrawer.innerHTML = freshDrawer.innerHTML;
        if (wasOpen || openDrawer) openOverlay(currentDrawer);
      }

      // The cart page renders its own totals server-side, so reload it.
      if (document.querySelector('[data-cart-items]')) {
        window.location.reload();
        return cart;
      }

      document.dispatchEvent(new CustomEvent('sitar:cart:updated', { detail: { cart: cart } }));
      return cart;
    });
  };

  // Add to cart (AJAX)
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('form[data-product-form]');
    if (!form) return;
    var drawerEnabled = document.querySelector('[data-cart-drawer]');
    if (!drawerEnabled) return; // fall through to normal POST when cart type is "page"

    event.preventDefault();
    var button = form.querySelector('[data-add-to-cart]');
    var errorNode = form.querySelector('[data-cart-error]');
    if (errorNode) errorNode.hidden = true;
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
    }

    Sitar.cart
      .add(new FormData(form))
      .then(function () {
        return Sitar.refreshCart(true);
      })
      .catch(function (error) {
        if (errorNode) {
          errorNode.textContent = (error && error.description) || 'Sorry, that could not be added to your cart.';
          errorNode.hidden = false;
        }
      })
      .then(function () {
        if (button) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-loading');
        }
      });
  });

  // Cart line quantity + remove
  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    remove.closest('.cart-item').classList.add('is-loading');
    Sitar.cart.change({ line: parseInt(remove.getAttribute('data-cart-remove'), 10), quantity: 0 }).then(function () {
      Sitar.refreshCart(false);
    });
  });

  document.addEventListener(
    'change',
    function (event) {
      var input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      var item = input.closest('.cart-item');
      if (item) item.classList.add('is-loading');
      Sitar.cart
        .change({ line: parseInt(input.getAttribute('data-cart-quantity'), 10), quantity: parseInt(input.value, 10) })
        .then(function () {
          Sitar.refreshCart(false);
        });
    },
    true
  );

  /* -------------------------------------------------------- variant picker */
  function VariantPicker(root) {
    this.root = root;
    this.form = root.closest('form[data-product-form]') || document.querySelector('form[data-product-form]');
    var dataNode = root.querySelector('[data-variant-json]');
    this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];
    this.sectionId = root.getAttribute('data-section-id');
    this.productUrl = root.getAttribute('data-product-url');
    this.root.addEventListener('change', this.onChange.bind(this));
  }

  VariantPicker.prototype.selectedOptions = function () {
    return Array.prototype.map.call(this.root.querySelectorAll('fieldset[data-option-index]'), function (fieldset) {
      var checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  };

  VariantPicker.prototype.onChange = function () {
    var selected = this.selectedOptions();

    // Update the visible option label
    this.root.querySelectorAll('fieldset[data-option-index]').forEach(function (fieldset, i) {
      var label = fieldset.querySelector('.variant-picker__value');
      if (label && selected[i]) label.textContent = selected[i];
    });

    var match = this.variants.find(function (variant) {
      return variant.options.every(function (option, i) {
        return option === selected[i];
      });
    });

    var idInput = this.form && this.form.querySelector('[name="id"]');
    var button = this.form && this.form.querySelector('[data-add-to-cart]');
    var priceNode = document.querySelector('[data-product-price]');

    if (!match) {
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.textContent = button.getAttribute('data-unavailable-text') || 'Unavailable';
      }
      return;
    }

    if (idInput) idInput.value = match.id;

    if (button) {
      if (match.available) {
        button.removeAttribute('aria-disabled');
        button.textContent = button.getAttribute('data-default-text') || 'Add to cart';
      } else {
        button.setAttribute('aria-disabled', 'true');
        button.textContent = button.getAttribute('data-soldout-text') || 'Sold out';
      }
    }

    if (priceNode) {
      var html = '<span class="price__regular">' + Sitar.money(match.price) + '</span>';
      if (match.compare_at_price && match.compare_at_price > match.price) {
        html =
          '<span class="price__sale">' +
          Sitar.money(match.price) +
          '</span><s class="price__compare">' +
          Sitar.money(match.compare_at_price) +
          '</s>';
        priceNode.classList.add('price--on-sale');
      } else {
        priceNode.classList.remove('price--on-sale');
      }
      priceNode.innerHTML = html;
    }

    // Featured media
    if (match.featured_media) {
      var gallery = document.querySelector('[data-product-gallery]');
      if (gallery) {
        var target = gallery.querySelector('[data-media-id="' + match.featured_media.id + '"]');
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }

    if (this.productUrl && window.history.replaceState) {
      window.history.replaceState({}, '', this.productUrl + '?variant=' + match.id);
    }
  };

  function initVariantPickers(scope) {
    (scope || document).querySelectorAll('[data-variant-picker]').forEach(function (root) {
      if (root._initialised) return;
      root._initialised = true;
      new VariantPicker(root);
    });
  }

  /* ------------------------------------------------------ product gallery */
  document.addEventListener('click', function (event) {
    var thumb = event.target.closest('[data-thumbnail]');
    if (!thumb) return;
    event.preventDefault();
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var target = gallery.querySelector('[data-media-id="' + thumb.getAttribute('data-thumbnail') + '"]');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    thumb.parentElement.querySelectorAll('[data-thumbnail]').forEach(function (el) {
      el.classList.toggle('is-active', el === thumb);
    });
  });

  /* ------------------------------------------------------ predictive search */
  function initPredictiveSearch() {
    var input = document.querySelector('[data-predictive-input]');
    var results = document.querySelector('[data-predictive-results]');
    if (!input || !results) return;

    var run = Sitar.debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }
      fetch(
        '/search/suggest?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product,collection,article&resources[limit]=6&section_id=predictive-search'
      )
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-predictive-results]');
          results.innerHTML = fresh ? fresh.innerHTML : '';
        })
        .catch(function () {
          results.innerHTML = '';
        });
    }, 280);

    input.addEventListener('input', run);
  }

  /* ------------------------------------------------------------- facets */
  document.addEventListener('change', function (event) {
    var form = event.target.closest('[data-facet-form]');
    if (!form) return;
    var params = new URLSearchParams(new FormData(form)).toString();
    var url = window.location.pathname + '?' + params;
    var wrapper = document.querySelector('[data-collection-results]');
    if (wrapper) wrapper.classList.add('is-loading');
    Sitar.fetchSection(form.getAttribute('data-section-id'), url).then(function (doc) {
      var fresh = doc.querySelector('[data-collection-results]');
      if (fresh && wrapper) wrapper.innerHTML = fresh.innerHTML;
      if (wrapper) wrapper.classList.remove('is-loading');
      if (window.history.replaceState) window.history.replaceState({}, '', url);
      initReveal();
    });
  });

  document.addEventListener('change', function (event) {
    var sort = event.target.closest('[data-sort-by]');
    if (!sort) return;
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    window.location.href = url.toString();
  });

  /* ----------------------------------------------------------- quick add */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    var id = button.getAttribute('data-quick-add');
    if (!id) return;
    button.classList.add('is-loading');
    var body = new FormData();
    body.append('id', id);
    body.append('quantity', 1);
    Sitar.cart
      .add(body)
      .then(function () {
        return Sitar.refreshCart(true);
      })
      .catch(function () {})
      .then(function () {
        button.classList.remove('is-loading');
      });
  });

  /* ----------------------------------------------------------------- video */
  document.addEventListener('click', function (event) {
    var play = event.target.closest('[data-video-play]');
    if (!play) return;
    event.preventDefault();
    var wrapper = play.closest('.video-section__wrapper');
    var template = wrapper.querySelector('template');
    if (!template) return;
    wrapper.innerHTML = '';
    wrapper.appendChild(template.content.cloneNode(true));
  });

  /* ------------------------------------------------------------------- tabs */
  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-tab]');
    if (!tab) return;
    var list = tab.closest('[data-tabs]');
    var scope = list.parentElement;
    var index = tab.getAttribute('data-tab');

    list.querySelectorAll('[data-tab]').forEach(function (t) {
      t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
    });
    scope.querySelectorAll('[data-tab-panel]').forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-tab-panel') !== index;
    });
    initReveal();
  });

  // Arrow-key navigation, which is what a tablist is expected to support.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    var tab = event.target.closest('[data-tab]');
    if (!tab) return;
    var tabs = Array.prototype.slice.call(tab.closest('[data-tabs]').querySelectorAll('[data-tab]'));
    var i = tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1);
    var next = tabs[(i + tabs.length) % tabs.length];
    next.focus();
    next.click();
  });

  /* -------------------------------------------------------------- countdown */
  function initCountdowns(scope) {
    (scope || document).querySelectorAll('[data-countdown]').forEach(function (el) {
      if (el._counting) return;
      el._counting = true;

      // Parsed as local time so the deadline means the same wall-clock moment
      // to every visitor, which is what a merchant typing "23:59" expects.
      var raw = (el.getAttribute('data-countdown') || '').trim().replace(' ', 'T');
      var target = new Date(raw).getTime();
      if (isNaN(target)) return;

      var out = {
        days: el.querySelector('[data-cd-days]'),
        hours: el.querySelector('[data-cd-hours]'),
        minutes: el.querySelector('[data-cd-minutes]'),
        seconds: el.querySelector('[data-cd-seconds]')
      };

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function tick() {
        var left = Math.max(0, target - Date.now());
        var s = Math.floor(left / 1000);
        if (out.days) out.days.textContent = Math.floor(s / 86400);
        if (out.hours) out.hours.textContent = pad(Math.floor(s / 3600) % 24);
        if (out.minutes) out.minutes.textContent = pad(Math.floor(s / 60) % 60);
        if (out.seconds) out.seconds.textContent = pad(s % 60);
        if (left <= 0 && el._timer) clearInterval(el._timer);
      }

      tick();
      el._timer = setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------------- product slider */
  function initProductSliders(scope) {
    (scope || document).querySelectorAll('[data-product-slider]').forEach(function (root) {
      if (root._initialised) return;
      root._initialised = true;

      var track = root.querySelector('[data-slider-track]');
      var section = root.closest('.section') || root.parentElement;
      var prev = section.querySelector('[data-slider-prev]');
      var next = section.querySelector('[data-slider-next]');
      var progress = section.querySelector('[data-slider-progress]');
      if (!track) return;

      function step() {
        var item = track.firstElementChild;
        if (!item) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      }

      function maxScroll() {
        return Math.max(track.scrollWidth - track.clientWidth, 0);
      }

      function sync() {
        var max = maxScroll();

        if (prev && next) {
          prev.disabled = track.scrollLeft <= 1;
          next.disabled = track.scrollLeft >= max - 1;
        }

        // The bar's width is the share of the row currently on screen, and it
        // travels across the rest — so it reads as a scrollbar, not a stepper.
        if (progress) {
          var visible = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
          var ratio = max > 0 ? track.scrollLeft / max : 0;
          var widthPct = Math.min(Math.max(visible, 0.08), 1) * 100;
          progress.style.width = widthPct + '%';
          progress.style.transform = 'translateX(' + (ratio * (100 - widthPct) / widthPct) * 100 + '%)';
        }
      }

      if (prev) prev.addEventListener('click', function () { stop(); track.scrollLeft -= step(); });
      if (next) next.addEventListener('click', function () { stop(); track.scrollLeft += step(); });

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });

      /* Autoplay ------------------------------------------------------------
         Opt-in per section. Advances one card at a time and rewinds at the
         end. Any sign of someone using the row — hover, focus, touch, wheel —
         stops it for good, so it never fights a person browsing. */
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var delay = parseInt(root.getAttribute('data-autoplay'), 10);
      var timer = null;

      function advance() {
        if (track.scrollLeft >= maxScroll() - 1) {
          track.scrollLeft = 0;
        } else {
          track.scrollLeft += step();
        }
      }

      function start() {
        if (timer || !delay || delay <= 0 || reduced) return;
        timer = window.setInterval(advance, delay);
      }

      function stop() {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
      }

      ['mouseenter', 'focusin', 'touchstart', 'wheel'].forEach(function (evt) {
        root.addEventListener(evt, stop, { passive: true });
      });

      sync();
      start();
    });
  }

  /* --------------------------------------------------------- hero slideshow */
  function HeroSlider(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));
    this.index = 0;
    this.timer = null;
    this.paused = false;

    if (this.slides.length < 2) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var delay = parseInt(root.getAttribute('data-autoplay'), 10);
    this.delay = reduced ? 0 : delay > 0 ? delay : 0;

    var self = this;
    var prev = root.querySelector('[data-hero-prev]');
    var next = root.querySelector('[data-hero-next]');
    if (prev) prev.addEventListener('click', function () { self.go(self.index - 1, true); });
    if (next) next.addEventListener('click', function () { self.go(self.index + 1, true); });

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.go(parseInt(dot.getAttribute('data-hero-dot'), 10), true);
      });
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.go(self.index - 1, true); }
      if (event.key === 'ArrowRight') { event.preventDefault(); self.go(self.index + 1, true); }
    });

    // Pausing on hover/focus keeps a slide readable while someone is reading it.
    root.addEventListener('mouseenter', function () { self.pause(); });
    root.addEventListener('mouseleave', function () { self.resume(); });
    root.addEventListener('focusin', function () { self.pause(); });
    root.addEventListener('focusout', function () {
      // focusout fires before focusin on the next element; only resume if focus
      // actually left the slider.
      window.setTimeout(function () {
        if (!root.contains(document.activeElement)) self.resume();
      }, 0);
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause(); else self.resume();
    });

    this.start();
  }

  HeroSlider.prototype.go = function (i, manual) {
    var total = this.slides.length;
    var next = ((i % total) + total) % total;

    this.slides.forEach(function (slide, n) {
      var active = n === next;
      slide.classList.toggle('is-active', active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.dots.forEach(function (dot, n) {
      dot.classList.toggle('is-active', n === next);
      dot.setAttribute('aria-selected', n === next ? 'true' : 'false');
    });

    this.index = next;
    if (manual) { this.stop(); this.start(); }
  };

  HeroSlider.prototype.pause = function () {
    this.paused = true;
    this.stop();
  };

  HeroSlider.prototype.resume = function () {
    this.paused = false;
    this.start();
  };

  HeroSlider.prototype.start = function () {
    if (!this.delay || this.timer || this.paused) return;
    var self = this;
    this.timer = setInterval(function () { self.go(self.index + 1); }, this.delay);
  };

  HeroSlider.prototype.stop = function () {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  };

  function initHeroSliders(scope) {
    (scope || document).querySelectorAll('[data-hero-slider]').forEach(function (root) {
      if (root._slider) return;
      root._slider = new HeroSlider(root);
    });
  }

  // Theme editor: jump to the slide the merchant just selected.
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest ? event.target.closest('[data-hero-slide]') : null;
    if (!slide) return;
    var root = slide.closest('[data-hero-slider]');
    if (!root || !root._slider) return;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
    root._slider.go(slides.indexOf(slide), true);
    root._slider.pause();
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var slide = event.target.closest ? event.target.closest('[data-hero-slide]') : null;
    if (!slide) return;
    var root = slide.closest('[data-hero-slider]');
    if (root && root._slider) root._slider.resume();
  });

  /* ------------------------------------------------------------------ init */
  function init() {
    initAnnouncement();
    initReveal();
    initStickyHeader();
    initCountryPicker();
    initVariantPickers();
    initPredictiveSearch();
    initHeroSliders();
    initProductSliders();
    initCountdowns();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    initVariantPickers(event.target);
    initAnnouncement();
    initReveal();
    initPredictiveSearch();
    initHeroSliders(event.target);
    initProductSliders(event.target);
    initCountdowns(event.target);
  });
})();
